import { useEffect, useState, useMemo } from "react";
import { Container, Table, Button, Breadcrumb } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { useAuth } from "@clerk/clerk-react";
import createAxiosInstance from "../utility/utils";

export default function StudentAttendanceView() {
  const { getToken } = useAuth();
  const axiosInstance = useMemo(
    () => createAxiosInstance(getToken),
    [getToken]
  );

  const [attendances, setAttendances] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const { studentId } = useParams();

  useEffect(() => {
    const fetchAttendance = async (studentId) => {
      try {
        const response = await axiosInstance.get(
          `/api/attendance/${studentId}`
        );
        // Filter out any records with null subject or teacher
        const validRecords = response.data.filter(
          (record) => record.subject && record.teacher
        );

        const sortedRecords = validRecords.sort((a, b) => {
          const aId = a.subject?._id || "";
          const bId = b.subject?._id || "";
          return aId.localeCompare(bId);
        });

        setAttendances(sortedRecords);
        setError(null);
      } catch (err) {
        setError("Failed to load attendance records");
        setAttendances([]);
      }
    };

    if (studentId) {
      fetchAttendance(studentId);
    }
  }, [studentId, axiosInstance]);

  const handleDownloadCSV = async () => {
    try {
      setDownloading(true);

      // Format the data for CSV with null checks
      const csvData = attendances.map((attendance) => ({
        "Subject Name": attendance.subject?.name || "N/A",
        "Teacher Name": attendance.teacher?.name || "Unknown Teacher",
        Date: attendance.date
          ? new Date(attendance.date).toLocaleDateString()
          : "N/A",
        Time: attendance.time || "N/A",
        "Attendance Status": attendance.attendanceStatus || "N/A",
      }));

      // Convert to CSV format with proper escaping
      const headers = Object.keys(csvData[0]).join(",");
      const rows = csvData.map((row) =>
        Object.values(row)
          .map((value) => `"${String(value).replace(/"/g, '""')}"`)
          .join(",")
      );
      const csvContent = [headers, ...rows].join("\n");

      // Create and download the file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `attendance_report_${studentId}_${new Date().toLocaleDateString()}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setError(null);
    } catch (error) {
      setError("Failed to download attendance report");
    } finally {
      setDownloading(false);
    }
  };

  // Helper function to get safe display values
  const getDisplayValue = (obj, property, fallback = "N/A") => {
    return obj?.[property] || fallback;
  };

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item href="/students-data">Students List</Breadcrumb.Item>
        <Breadcrumb.Item active>Student Attendance Records</Breadcrumb.Item>
      </Breadcrumb>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Student Attendance Records</h3>
        {attendances.length > 0 && (
          <Button
            variant="primary"
            onClick={handleDownloadCSV}
            disabled={downloading}
          >
            {downloading ? "Downloading..." : "Download CSV"}
          </Button>
        )}
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Subject Name</th>
            <th>Teacher Name</th>
            <th>Date</th>
            <th>Time</th>
            <th>Attendance Status</th>
          </tr>
        </thead>
        <tbody>
          {attendances.length > 0 ? (
            attendances.map((ele, i) => {
              const isSameSubject =
                i > 0 && ele.subject?._id === attendances[i - 1].subject?._id;

              return (
                <tr key={i}>
                  {!isSameSubject ? (
                    <td
                      rowSpan={
                        attendances.filter(
                          (item) => item.subject?._id === ele.subject?._id
                        ).length
                      }
                    >
                      {getDisplayValue(ele.subject, "name", "Unknown Subject")}
                    </td>
                  ) : null}
                  <td>
                    {getDisplayValue(ele.teacher, "name", "Unknown Teacher")}
                  </td>
                  <td>{ele.date || "N/A"}</td>
                  <td>{ele.time || "N/A"}</td>
                  <td>{ele.attendanceStatus || "N/A"}</td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="5">{error || "No attendance records found"}</td>
            </tr>
          )}
        </tbody>
      </Table>
    </Container>
  );
}
